<script setup lang="ts">
/**
 * NotificationsSection — personal notification preferences.
 *
 * Scoped to the current board member only. Calls PATCH /v1/board/members/{id}
 * on toggle change (not the settings endpoints).
 */
import { ref } from 'vue';
import BaseCard from '@/components/ui/BaseCard.vue';
import { api } from '@/api/client';

interface NotifRow {
  key: string;
  title: string;
  desc: string;
}

const rows: NotifRow[] = [
  {
    key: 'sms',
    title: 'SMS text alerts',
    desc: 'Receive critical and high-priority alerts via text message.',
  },
  {
    key: 'email',
    title: 'Email digests',
    desc: 'Daily summary + weekly board report sent to your email.',
  },
  {
    key: 'push',
    title: 'Push notifications',
    desc: 'Browser and mobile app push notifications.',
  },
  {
    key: 'quiet',
    title: 'Quiet hours',
    desc: '10:00 PM – 7:00 AM CT · Critical alerts override.',
  },
];

// Local toggle state — loaded from the current member (stub: defaults to true)
const enabled = ref<Record<string, boolean>>({
  sms: true,
  email: true,
  push: true,
  quiet: true,
});

const saveError = ref<string | null>(null);

async function onToggle(key: string) {
  const prev = enabled.value[key] ?? false;
  enabled.value[key] = !prev;
  saveError.value = null;

  try {
    // Build notificationMethods from current state
    const methods: string[] = [];
    if (enabled.value.sms) methods.push('sms');
    if (enabled.value.email) methods.push('email');
    if (enabled.value.push) methods.push('push');

    // PATCH current member's notification prefs
    const { error } = await api.PATCH('/v1/board/members/{id}', {
      params: { path: { id: 'me' } },
      body: { notificationMethodsJson: JSON.stringify(methods) },
    });
    if (error) throw new Error(JSON.stringify(error));
  } catch {
    // Rollback on error
    enabled.value[key] = prev;
    saveError.value = 'Failed to update notification preferences.';
  }
}
</script>

<template>
  <BaseCard id="notifications" padding="none" data-testid="notifications-section">
    <div class="p-4 sm:p-6 border-b border-border">
      <h3 class="text-base font-semibold text-fg">My notifications</h3>
      <p class="text-sm text-muted mt-0.5">How and when HOA Watch reaches you.</p>
    </div>

    <div class="p-4 sm:p-6">
      <div
        v-for="(row, i) in rows"
        :key="row.key"
        class="flex items-center justify-between gap-4 py-3"
        :class="i < rows.length - 1 ? 'border-b border-border' : ''"
      >
        <div class="grow min-w-0">
          <div class="text-sm font-medium text-fg">{{ row.title }}</div>
          <div class="text-xs text-muted mt-0.5">{{ row.desc }}</div>
        </div>

        <button
          role="switch"
          :aria-checked="enabled[row.key]"
          :data-testid="`notif-toggle-${row.key}`"
          class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-600"
          :class="enabled[row.key] ? 'bg-navy-800' : 'bg-gray-300'"
          @click="onToggle(row.key)"
        >
          <span
            class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform"
            :class="enabled[row.key] ? 'translate-x-4' : 'translate-x-0'"
          />
        </button>
      </div>

      <p v-if="saveError" class="text-sm text-red-600 mt-2" role="alert">{{ saveError }}</p>
    </div>
  </BaseCard>
</template>
